import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { useEffect, useState } from "react";
import { auth, db } from "../firebase/config";
import Header from "../Components/Header"
import CommentModal from "../Components/kanban/CommentModal"
import KanbanColumn from "../Components/kanban/KanbanColumn";
import TaskForm from "../Components/kanban/TaskForm"
import ConfirmationModal from "../Components/kanban/ConfirmationModal"
import AddColumnModal from "../Components/kanban/AddColumnModal"
function Kanban({ isSidebarOpen, setIsSidebarOpen }) {
  const [columns, setColumns] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);

  const [userId, setUserId] = useState(null);

  const [showTaskForm, setShowTaskForm] = useState(false);
  const [currentTask, setCurrentTask] = useState(null);
  const [currentColumnId, setCurrentColumnId] = useState(null);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [columnToDelete, setColumnToDelete] = useState(null);

  const [showCommentModal, setShowCommentModal] = useState(false);
  const [selectedTaskForComments, setSelectedTaskForComments] = useState(null);
  const [showAddColumnModal, setShowAddColumnModal] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // User is signed in.
        setUserId(user.uid);
      }
    });

    return () => unsubscribe();
  }, []);

  // Fetch data once Firebase is initialized and user auth state is known
  useEffect(() => {
    if (!userId) {
      console.log(
        "Authentication not ready or user ID not available. Skipping data fetch."
      );
      return;
    }

    const columnsCollectionPath = `kanban_columns`;
    const tasksCollectionPath = `kanban_tasks`;
    const usersCollectionPath = `users`;

    // Set up real-time listeners for columns
    const columnsRef = collection(db, columnsCollectionPath);
    const unsubscribeColumns = onSnapshot(
      query(columnsRef),
      (snapshot) => {
        const fetchedColumns = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setColumns(
          fetchedColumns.sort((a, b) => (a.order || 0) - (b.order || 0))
        );
      },
      (error) => {
        console.error("Error fetching columns:", error);
      }
    );

    // Set up real-time listeners for tasks
    const tasksRef = collection(db, tasksCollectionPath);
    const unsubscribeTasks = onSnapshot(
      query(tasksRef),
      (snapshot) => {
        const fetchedTasks = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setTasks(fetchedTasks);
      },
      (error) => {
        console.error("Error fetching tasks:", error);
      }
    );

    // Set up real-time listeners for users (for assignees)
    const usersRef = collection(db, usersCollectionPath);
    const unsubscribeUsers = onSnapshot(
      query(usersRef, where("role", '==', "sales")),
      (snapshot) => {
        const fetchedUsers = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          name: doc.data().displayName || doc.id, // Use displayName if available, otherwise fallback to ID
        }));
        setUsers(fetchedUsers);
      },
      (error) => {
        console.error("Error fetching users for assignees:", error);
      }
    );

    return () => {
      unsubscribeColumns();
      unsubscribeTasks();
      unsubscribeUsers();
    };
  }, [userId]); // Depend only on auth readiness and userId

  // CRUD Operations for Columns
  const handleAddColumnClick = () => {
    setShowAddColumnModal(true);
  };

  const handleAddColumnSubmit = async (newColumnTitle) => {
    try {
      await addDoc(collection(db, `kanban_columns`), {
        title: newColumnTitle,
        order: columns.length, // Assign an order based on current number of columns
      });
      console.log("Column added successfully.");
      setShowAddColumnModal(false); // Close the modal after successful addition
    } catch (error) {
      console.error("Error adding column:", error);
    }
  };

  const confirmDeleteColumn = (columnId) => {
    setColumnToDelete(columnId);
    setShowConfirmModal(true);
  };

  const handleDeleteColumn = async () => {
    try {
      // Delete the column document
      await deleteDoc(doc(db, `kanban_columns`, columnToDelete));
      // Filter out tasks associated with the deleted column and delete them
      const tasksToDelete = tasks.filter(
        (task) => task.columnId === columnToDelete
      );
      for (const task of tasksToDelete) {
        await deleteDoc(doc(db, `kanban_tasks`, task.id));
      }
      console.log("Column and associated tasks deleted successfully.");
      setShowConfirmModal(false); // Close confirmation modal
      setColumnToDelete(null); // Reset column to delete state
    } catch (error) {
      console.error("Error deleting column:", error);
    }
  };

  // CRUD Operations for Tasks
  const handleAddTask = (columnId) => {
    setCurrentTask(null); // Clear any previously selected task
    setCurrentColumnId(columnId); // Set the column for the new task
    setShowTaskForm(true); // Open the task form modal
  };

  const handleEditTask = (task) => {
    setCurrentTask(task); // Set the task to be edited
    setCurrentColumnId(task.columnId); // Retain its original column ID
    setShowTaskForm(true); // Open the task form modal
  };

  const handleSaveTask = async (taskData) => {
    try {
      if (taskData.id) {
        // If taskData has an ID, it's an existing task, so update it
        await updateDoc(doc(db, `kanban_tasks`, taskData.id), taskData);
        console.log("Task updated successfully.");
      } else {
        // Otherwise, it's a new task, so add it
        await addDoc(collection(db, `kanban_tasks`), {
          ...taskData,
          columnId: currentColumnId, // Assign the column ID
          createdAt: new Date().toISOString(), // Record creation timestamp
          commentCount: 0, // Initialize comment count
          attachmentCount: 0, // Initialize attachment count
        });
        console.log("Task added successfully.");
      }
      setShowTaskForm(false); // Close the task form modal
      setCurrentTask(null); // Clear current task
      setCurrentColumnId(null); // Clear current column ID
    } catch (error) {
      console.error("Error saving task:", error);
    }
  };

  // Drag and Drop Logic for tasks between columns
  const handleDropTask = async (taskId, sourceColumnId, targetColumnId, title) => {
    if (sourceColumnId !== targetColumnId) {
      // Only update if the task is moved to a different column
      let status; 
      if(title === "Leads à contacter") {
        status = "à contacter";

      } else if (title === "A rappeler"){
        status = "à rappeler";
      } else {
        status = "closé";
      }
      try {
        await updateDoc(doc(db, `kanban_tasks`, taskId), {
          columnId: targetColumnId,
          status: status,
        });
        console.log(
          `Task ${taskId} moved from ${sourceColumnId} to ${targetColumnId}`
        );
      } catch (error) {
        console.error("Error moving task:", error);
      }
    }
  };

  // Comment Functionality
  const handleViewCommentsClick = (task) => {
    setSelectedTaskForComments(task); // Set the task for which comments will be viewed
    setShowCommentModal(true); // Open the comment modal
  };

  const handleCommentAdded = async (taskId) => {
    const taskRef = doc(db, `kanban_tasks`, taskId);
    try {
      // Find the current task to increment its comment count
      const currentTask = tasks.find((t) => t.id === taskId);
      const newCommentCount = (currentTask?.commentCount || 0) + 1;

      // Update the comment count in Firestore
      await updateDoc(taskRef, {
        commentCount: newCommentCount,
      });
      console.log("Task comment count updated.");
    } catch (error) {
      console.error("Error updating comment count:", error);
    }
  };

  return (
    <div className="min-h-screen text-white font-inter flex flex-col">
      <div className="flex flex-col">
        <Header
          PageName={"Kanban"}
          isSidebarOpen={isSidebarOpen}
          setIsSidebarOpen={setIsSidebarOpen}
        />
      </div>
      <div className="bg-gray-900 flex-1 overflow-auto">
        <style>
          {`
            @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
            body {
              font-family: 'Inter', sans-serif;
            }
            /* Custom scrollbar for horizontal column overflow */
            .kanban-columns-container::-webkit-scrollbar {
              height: 8px;
            }
            .kanban-columns-container::-webkit-scrollbar-track {
              background: #1f2937; /* bg-gray-800 */
              border-radius: 10px;
            }
            .kanban-columns-container::-webkit-scrollbar-thumb {
              background: #4b5563; /* bg-gray-600 */
              border-radius: 10px;
            }
            .kanban-columns-container::-webkit-scrollbar-thumb:hover {
              background: #6b7280; /* bg-gray-500 */
            }

            /* Custom scrollbar for vertical task list overflow */
            .custom-scrollbar::-webkit-scrollbar {
              width: 6px;
            }
            .custom-scrollbar::-webkit-scrollbar-track {
              background: #374151; /* bg-gray-700 */
              border-radius: 10px;
            }
            .custom-scrollbar::-webkit-scrollbar-thumb {
              background: #60a5fa; /* blue-400 */
              border-radius: 10px;
            }
            .custom-scrollbar::-webkit-scrollbar-thumb:hover {
              background: #3b82f6; /* blue-500 */
            }
            `}
        </style>

        {userId ? ( // Render content only if auth is ready and userId is available
          <div className="flex-1 overflow-x-auto pb-4 kanban-columns-container">
            <div className="flex space-x-4 h-full items-start">
              {columns.map((column) => (
                <KanbanColumn
                  key={column.id}
                  column={column}
                  tasks={tasks
                    .filter((task) => task.columnId === column.id)
                    .sort(
                      (a, b) => new Date(b.createdAt.toDate()).getTime() - new Date(a.createdAt.toDate()).getTime()
                    )}
                  onAddTaskClick={handleAddTask}
                  onEditTaskClick={handleEditTask}
                  onDropTask={handleDropTask}
                  onDeleteColumn={confirmDeleteColumn}
                  onViewCommentsClick={handleViewCommentsClick}
                />
              ))}

              {/* Add Column Button */}
              <div className="flex-shrink-0 w-80 bg-gray-800 rounded-lg p-4 m-2 shadow-lg flex items-center justify-center border-2 border-dashed border-gray-600 hover:border-blue-500 transition-colors duration-200 cursor-pointer h-auto min-h-[calc(100vh-180px)]">
                <button
                  onClick={handleAddColumnClick}
                  className="w-full h-full flex items-center justify-center text-gray-400 hover:text-blue-400 font-bold text-lg"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-8 w-8 mr-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                    />
                  </svg>
                  Add New Column
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex justify-center items-center h-full text-gray-400">
            Loading Kanban board...
          </div>
        )}

        {showTaskForm && (
          <TaskForm
            task={currentTask}
            onClose={() => {
              setShowTaskForm(false);
              setCurrentTask(null);
              setCurrentColumnId(null);
            }}
            onSubmit={handleSaveTask}
            users={users}
          />
        )}

        {showConfirmModal && (
          <ConfirmationModal
            message="Are you sure you want to delete this column and all its tasks? This action cannot be undone."
            onConfirm={handleDeleteColumn}
            onCancel={() => {
              setShowConfirmModal(false);
              setColumnToDelete(null);
            }}
          />
        )}

        {showCommentModal && selectedTaskForComments && (
          <CommentModal
            task={selectedTaskForComments}
            onClose={() => {
              setShowCommentModal(false);
              setSelectedTaskForComments(null);
            }}
            onCommentAdded={handleCommentAdded}
            db={db} // Pass the imported 'db'
          />
        )}
        {showAddColumnModal && (
          <AddColumnModal
            onClose={() => setShowAddColumnModal(false)}
            onSubmit={handleAddColumnSubmit}
          />
        )}
      </div>
    </div>
  );
}

export default Kanban;
